<template>
  <b-container>
    <div id="control-header" style="padding: 10px; font-size: large; font-weight: bold">

      <b-row>
        <b-col cols="9">
          Fertilizer Data
        </b-col>
        <b-col cols="3" class="text-right">
          <b-button size="sm" @click="requestDownload()">
            Export Fertilizer Data
          </b-button>
        </b-col>
      </b-row>
    </div>
    <ag-grid-vue style="width: 100%; height: 400px"
                 class="ag-theme-balham"
                 :gridOptions="topGridOptions"
                 :columnDefs="columnDefs"
                 :rowData="rowData"
                 :defaultColDef="defaultColDef"
                 :enterMovesDownAfterEdit="true"
                 @cell-editing-stopped="onCellEditingStopped"
    >
    </ag-grid-vue>
    <ag-grid-vue style="width: 100%; height: 40px;"
                 class="ag-theme-balham"
                 :gridOptions="bottomGridOptions"
                 :headerHeight="0"
                 :columnDefs="bottomColumnDefs"
                 :rowData="bottomDataCalc"
                 :rowStyle="rowStyle"
    ></ag-grid-vue>
  </b-container>
</template>

<script>
import {AgGridVue} from "ag-grid-vue";
import NumericEditor from "@/numericEditor";
import {roundColumnValue} from "@/MetricUtils";
import {FileUtils} from "@/lib/FileUtils";

const SOLID = "solid";
const LIQUID = "liquid";
const COMPOST = "compost";
const MANURE = "manure";

const SizeEnum = {
  EM1: SOLID,
  EM2: LIQUID,
  EM3: COMPOST,
  EM4: MANURE,
};

window.numberParser = function numberParser(params) {
  let parsedNumber = Number(params.newValue);

  return isNaN(parsedNumber) ? "ERROR" : parsedNumber;
};

export default {
  name: "VineyardFertilizerMetricTable",
  props: {rows: Array, year: String, id: String, groupId: String},
  components: {
    AgGridVue
  },

  data() {
    return {
      metricYearVal: this.year,
      gridApi: null,
      gridColumnApi: null,
      bottomColumnDefs: null,
      columnDefs: null,
      defaultColDef: null,
      pinnedTopRowData: null,
      pinnedBottomRowData: null,
      topGridOptions: null,
      bottomGridOptions: null,
      rowData: this.rows,
      bottomData: [],
      rowStyle: {fontWeight: 'bold'},
      enteredValueDecimalPlaces: 0,
      totalsValueDecimalPlaces: 0,
      metricId: this.id,
      myGroupId: this.groupId,
      isViewer: localStorage.getItem('roles') !== null && localStorage.getItem('roles').includes('VIEWER')
    }
  },
  mounted() {
    this.gridApi = this.topGridOptions.api;
    this.gridColumnApi = this.topGridOptions.columnApi;
    //this.bottomData = this.bottomDataCalc()
    //this.$nextTick(this.sendInitialMetricsUpdate(this.metricYearVal));
  },
  watch: {
    rows: function (newVal, oldVal) {
      this.rowData = newVal;
    },
    year: function (newVal, oldVal) {
      this.metricYearVal = newVal;
    },
    id: function (newVal, oldVal) {
      this.metricId = newVal;
    },
    groupId: function (newVal, oldVal) {
      this.myGroupId = newVal;
    }
  },
  beforeMount() {

    this.columnDefs = [
      {
        headerName: 'Month', field: 'month', pinned: "left",
        lockPinned: true,
        width: 100,
        editable: false
      },
      {
        headerName: 'Year', field: 'year', pinned: "left",
        lockPinned: true,
        width: 75,
        editable: false
      },
      {
        headerName: 'Solid (Lbs)',
        field: SOLID,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      }, {
        headerName: 'Liquid (Gallons)',
        field: LIQUID,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      }, {
        headerName: 'Compost (Tons)',
        field: COMPOST,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      },
      {
        headerName: 'Manure (Tons)',
        field: MANURE,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      }
    ];
    this.bottomColumnDefs = [
      {
        headerName: 'Month', field: 'month', pinned: "left",
        lockPinned: true,
        width: 100,
        editable: false
      },
      {
        headerName: 'Year', field: 'year', pinned: "left",
        lockPinned: true,
        width: 75,
        editable: false
      },
      {
        headerName: 'Solid (Lbs)',
        field: SOLID,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      }, {
        headerName: 'Liquid (Gallons)',
        field: LIQUID,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      }, {
        headerName: 'Compost (Tons)',
        field: COMPOST,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      },
      {
        headerName: 'Manure (Tons)',
        field: MANURE,
        valueParser: this.numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      }
    ];
    this.bottomData = [
      {
        month: 'Total',
        year: '******',
        solid: 'a lot',
        liquid: 'not as much',
        compost: 'a little more',
        manure: '=ctx.sum("solarProductivity")'
      }
    ];

    this.defaultColDef = {
      editable: true,
      resizable: true
    };

    this.pinnedTopRowData = getPinnedTopData();
    this.pinnedBottomRowData = getPinnedBottomData();

    this.topGridOptions = {alignedGrids: [], suppressHorizontalScroll: true};
    this.bottomGridOptions = {
      alignedGrids: [], enableCellExpressions: true, context: {
        theNumber: 4
      }
    };
    this.topGridOptions.alignedGrids.push(this.bottomGridOptions);
    this.bottomGridOptions.alignedGrids.push(this.topGridOptions);

    this.frameworkComponents = {
      numericEditor: NumericEditor
    };
    console.log("Fertilizer Row Data in FertilizerMetricTable is above.")
  },
  computed: {
    bottomDataCalc: function () {

      let self = this;
      let bottomRowsMap = new Map();
      let columnNames = [];
      Object.values(SizeEnum).map(key => {
        let result = 0;
        Object.values(self.rowData).forEach(row => {

          result += row[key];

        });
        columnNames.push(result);
      });

      let bottom =
          {
            month: "Total",
            year: '******',
            solid: 0,
            liquid: 0,
            compost: 0,
            manure: 0
          };
      if (bottomRowsMap !== undefined) {
        bottom = {
          month: "Total",
          year: '******',
          solid: Number(columnNames[0]),
          liquid: Number(columnNames[1]),
          compost: Number(columnNames[2]),
          manure: Number(columnNames[3])
        };
      }

      console.log(bottom);

      return [bottom];
    }
  },
  methods: {
    sendInitialMetricsUpdate(year) {
      let self = this;
      let solid = 0;
      let liquid = 0;
      let compost = 0;
      let manure = 0;

      if (this.gridApi !== undefined) {
        //console.log("Traverse GridApi");
        this.gridApi.forEachNode(function (node) {
          if (node.data["year"] === year) {
            //total += parseInt(node.data[event.colDef.field], 10);
            solid += self.safeParseNumber(node, "solid");
            liquid += self.safeParseNumber(node, "liquid");
            compost += self.safeParseNumber(node, "compost");
            manure += self.safeParseNumber(node, "manure");
          }
        });
      }
      this.calculateAndUpdateTotalsVuex(solid, liquid, compost, manure, "gridReady", event);
    },
    onCellEditingStopped(event) {
      let self = this;
      let totalSolid = 0;
      let totalLiquid = 0;
      let totalCompost = 0;
      let totalManure = 0;
      console.log("Grid Api");
      console.log(self.gridApi);
      console.log(this.gridApi);
      if (self.gridApi !== undefined) {
        self.gridApi.forEachNode(function (node) {
          if (node.data["year"] === event.data["year"]) {
            totalSolid += Number(self.safeParseNumber(node, "solid"));
            totalLiquid += Number(self.safeParseNumber(node, "liquid"));
            totalCompost += Number(self.safeParseNumber(node, "compost"));
            totalManure += Number(self.safeParseNumber(node, "manure"));
          }
        });
      }

      //TODO This is where I re-enable Database update
      this.updateFertilizerMetrics(self);

      this.calculateAndUpdateTotalsVuex(totalSolid, totalLiquid, totalCompost, totalManure, "cellStoppedEditing", event);
      this.bottomGridOptions.api.redrawRows({rowNodes: [this.bottomGridOptions.api.getRowNode('0')]})
    },
    // calculateAndEmitTotals: function (totalSolid, totalLiquid, totalCompost, totalManure, when, event) {
    //   console.log("Total: " + totalSolid);
    //   this.bottomGridOptions.api.getRowNode('0').data["solid"] = totalSolid;
    //   this.bottomGridOptions.api.getRowNode('0').data["liquid"] = totalLiquid;
    //   this.bottomGridOptions.api.getRowNode('0').data["compost"] = totalCompost;
    //   this.bottomGridOptions.api.getRowNode('0').data["manure"] = totalManure;
    //
    //   let fertilizerEmission = {
    //     gridType: "fertilizer",
    //     solid: totalSolid,
    //     liquid: totalLiquid,
    //     compost: totalCompost,
    //     manure: totalManure,
    //     year: this.metricYearVal, //event.data["year"],
    //     when: when
    //   };
    //   console.log(fertilizerEmission);
    //   Bus.$emit('vineyard-fertilizer-edited', fertilizerEmission);
    // },
    calculateAndUpdateTotalsVuex: function (totalSolid, totalLiquid, totalCompost, totalManure, when, event) {
      console.log("Total: " + totalSolid);
      this.bottomGridOptions.api.getRowNode('0').data["solid"] = totalSolid;
      this.bottomGridOptions.api.getRowNode('0').data["liquid"] = totalLiquid;
      this.bottomGridOptions.api.getRowNode('0').data["compost"] = totalCompost;
      this.bottomGridOptions.api.getRowNode('0').data["manure"] = totalManure;

      let fertilizerEmission = {
        gridType: "fertilizer",
        solid: totalSolid,
        liquid: totalLiquid,
        compost: totalCompost,
        manure: totalManure,
        year: this.metricYearVal, //event.data["year"],
        when: when
      };
      console.log(fertilizerEmission);
      this.$store.commit('solid', totalSolid);
      this.$store.commit('liquid', totalLiquid);
      this.$store.commit('compost2', totalCompost);
      this.$store.commit('manure', totalManure);
    },
    updateFertilizerMetrics: function (self) {
      if (self.gridApi !== undefined) {
        let year = {};
        let powerMetricsMonth = [];
        self.gridApi.forEachNode(function (node) {
          year = node.data["year"];
          powerMetricsMonth.push({
            month: node.data["month"],
            solid: self.safeParseNumber(node, "solid"),
            liquid: self.safeParseNumber(node, "liquid"),
            compost: self.safeParseNumber(node, "compost"),
            manure: self.safeParseNumber(node, "manure")
          })
        });
        let metricYear = {
          _id: this.metricId,
          year: year,
          groupId: this.myGroupId,
          months: powerMetricsMonth
        };
        console.log("Metrics Year");
        console.log(JSON.stringify(metricYear));
        this.$http.put(`/metrics/vineyard/fertilizer/${this.metricId}`, JSON.stringify(metricYear), {
          headers: {
            'content-type': 'application/json'
          }
        }).then(response => {
          console.log(response);
          // TODO Add Notification for Updated Metrics
          if (self.metricId === null) {
            self.metricId = response.data;
          }
        }).catch(error => {
          console.log("Error Putting Data");
          console.error(error);
        })
      }
    },
    safeParseNumber: function (node, indexName) {
      let isNotANumber = isNaN(parseInt(node.data[indexName]));
      //console.log(`Is Not a Number: ${isNotANumber}, Node Value: ${node.data[indexName]} returned Value: ${isNotANumber ? 0 : parseInt(node.data[indexName])}`);
      return isNotANumber ? 0 : parseInt(node.data[indexName]);
    },
    requestDownload() {
      let self = this;
      this.$http.get(`metrics/download/${self.myGroupId}/${self.metricYearVal}/fertilizer/vineyard`, {responseType: "blob"})
          .then(async response => {
            FileUtils.saveExcelToDisk(response);
          });
    },
  }
}

function getPinnedTopData() {
  return [
    {
      firstName: "##",
      lastName: "##",
      gender: "##",
      address: "##",
      mood: "##",
      country: "##"
    }
  ];
}

function getPinnedBottomData() {
  return [
    {
      firstName: "##",
      lastName: "##",
      gender: "##",
      address: "##",
      mood: "##",
      country: "##"
    }
  ];
}
</script>

<style scoped>

</style>
